import { useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useHotkeys } from 'react-hotkeys-hook';
import { Eye, EyeOff } from 'lucide-react';
import { useAgents, useDeleteAgent, useUpdateAgent } from '../../hooks/useAgents';
import { useModelLookup } from '../../hooks/useModels';
import { useAgentRoles } from '../../hooks/useAgentRoles';
import { useEntityPermissions } from '../../hooks/useEntityPermissions';
import { usePlatformCortexLatest } from '../../hooks/usePlatformCortex';
import { useSpaceId } from '../../contexts/SpaceContext';
import { routes } from '../../lib/routes';
import { Badge, Button, ConfirmModal, ControlledTabs, Input, Table, getStatusVariant } from '@/components/ui';
import { useToast } from '../../components/ui/Toast';
import type { Agent } from '../../types';
import { isPlatformEntity } from '../../types';
import type { AgentRole } from '../../utils/agent-roles';
import AgentActionsMenu from './AgentActionsMenu';
import PublishAgentModal from './PublishAgentModal';
import ScheduleList from './ScheduleList';
import SubscriptionList from './SubscriptionList';

const TABS = [
  { id: 'agents', label: 'Agents' },
  { id: 'schedules', label: 'Schedules' },
  { id: 'subscriptions', label: 'Subscriptions' },
];

const ROLE_CHIPS: { label: string; role: AgentRole }[] = [
  { label: 'Coordinators', role: 'coordinator' },
  { label: 'Standalone', role: 'standalone' },
  { label: 'Sub-agents', role: 'sub-agent' },
];

export default function AgentList() {
  const navigate = useNavigate();
  const spaceId = useSpaceId();
  const { success, error: showError } = useToast();
  const { data, isLoading } = useAgents();
  const deleteAgent = useDeleteAgent();
  const updateAgent = useUpdateAgent();
  const { canEdit, canDelete } = useEntityPermissions();
  const modelLookup = useModelLookup();
  const { data: latestCortex } = usePlatformCortexLatest();

  const location = useLocation();
  const activeTab = location.pathname.endsWith('/schedules')
    ? 'schedules'
    : location.pathname.endsWith('/subscriptions')
      ? 'subscriptions'
      : 'agents';
  const setActiveTab = (tab: string) =>
    navigate(
      tab === 'agents'
        ? routes.console.spaceAgents(spaceId)
        : tab === 'schedules'
          ? routes.console.spaceAgentSchedules(spaceId)
          : routes.console.spaceAgentSubscriptions(spaceId),
      { replace: true },
    );

  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [enabledRoles, setEnabledRoles] = useState<Set<AgentRole>>(
    new Set(['coordinator', 'standalone'])
  );

  // `Esc` focuses the agent search. Routed via react-hotkeys-hook so it shows
  // up in the Shift+? cheatsheet and inherits its built-in skip for
  // input/textarea/contentEditable targets.
  useHotkeys(
    'escape',
    (event) => {
      event.preventDefault();
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    },
    {
      description: 'Focus agent search',
      metadata: { group: 'Agents' },
    },
  );
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    agent: Agent | null;
  }>({ isOpen: false, agent: null });
  // The row menu's Publish action opens the shared dialog for that agent.
  const [publishTarget, setPublishTarget] = useState<Agent | null>(null);
  // Which agent's activate/deactivate mutation is in flight, so only that
  // row's menu trigger goes disabled.
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const agents = data?.items || [];
  const rolesMap = useAgentRoles(agents);

  function toggleRole(role: AgentRole) {
    setEnabledRoles((prev) => {
      const next = new Set(prev);
      if (next.has(role)) {
        next.delete(role);
      } else {
        next.add(role);
      }
      return next;
    });
  }

  const filteredAgents = agents.filter((agent) => {
    const matchesSearch =
      agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      agent.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const roles = rolesMap.get(agent.agentId);
    const matchesRole = !roles || [...roles].some((r) => enabledRoles.has(r));
    return matchesSearch && matchesRole;
  });

  const handleDelete = async () => {
    if (!deleteModal.agent) return;

    try {
      await deleteAgent.mutateAsync(deleteModal.agent.agentId);
      success('Agent deleted successfully');
      setDeleteModal({ isOpen: false, agent: null });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete agent');
    }
  };

  const handleToggleStatus = async (agent: Agent) => {
    const next = agent.status === 'active' ? 'inactive' : 'active';
    setTogglingId(agent.agentId);
    try {
      await updateAgent.mutateAsync({ agentId: agent.agentId, input: { status: next } });
      success(next === 'active' ? 'Agent activated' : 'Agent deactivated');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update agent status');
    } finally {
      setTogglingId(null);
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (agent: Agent) => (
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium text-foreground">{agent.name}</p>
            {isPlatformEntity(agent) && (
              <Badge variant="info">Platform</Badge>
            )}
            {rolesMap.get(agent.agentId)?.has('coordinator') && (
              <Badge variant="purple">Coordinator</Badge>
            )}
            {rolesMap.get(agent.agentId)?.has('sub-agent') && (
              <Badge variant="teal">Sub-agent</Badge>
            )}
            {agent.provenance?.source === 'plugin' && (
              <Badge variant="info">Plugin</Badge>
            )}
            {agent.runtime === 'claude' && (
              <Badge variant="purple">Claude Runtime</Badge>
            )}
          </div>
          <p className="text-muted-foreground text-xs mt-0.5 line-clamp-2">
            {agent.description || 'No description'}
          </p>
        </div>
      ),
    },
    {
      key: 'model',
      header: 'Model',
      className: 'whitespace-nowrap',
      render: (agent: Agent) => (
        <span className="text-muted-foreground">
          {modelLookup[agent.modelId]?.displayName ?? agent.modelId}
        </span>
      ),
    },
    {
      key: 'version',
      header: 'Version',
      className: 'whitespace-nowrap',
      render: (agent: Agent) => {
        const cortexOutdated =
          agent.platformCortexVersion != null &&
          latestCortex != null &&
          agent.platformCortexVersion < latestCortex.version;
        return (
          <span className="text-muted-foreground inline-flex items-center gap-1.5">
            {agent.latestVersion ? `v${agent.latestVersion}` : 'Draft'}
            {cortexOutdated && (
              <Badge variant="warning" className="text-[10px] px-1.5 py-0">cortex</Badge>
            )}
          </span>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      className: 'whitespace-nowrap',
      render: (agent: Agent) => (
        <Badge variant={getStatusVariant(agent.status)}>{agent.status}</Badge>
      ),
    },
    {
      key: 'updatedAt',
      header: 'Last Updated',
      className: 'whitespace-nowrap',
      render: (agent: Agent) => (
        <span className="text-muted-foreground">
          {new Date(agent.updatedAt).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      // The row itself navigates (see the Table's onRowClick), so the menu has
      // to stop clicks from bubbling or opening it also opens the agent.
      render: (agent: Agent) => (
        <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
          <AgentActionsMenu
            agent={agent}
            spaceId={spaceId}
            canEdit={canEdit}
            canDelete={canDelete(agent)}
            onToggleStatus={() => handleToggleStatus(agent)}
            onPublish={() => setPublishTarget(agent)}
            onDelete={() => setDeleteModal({ isOpen: true, agent })}
            isBusy={togglingId === agent.agentId}
          />
        </div>
      ),
      className: 'text-right whitespace-nowrap w-10',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Agents</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage your AI agents and their configurations
          </p>
        </div>
        {activeTab === 'agents' && (
          <Link to={routes.console.spaceAgentNew(spaceId)}>
            <Button>Create Agent</Button>
          </Link>
        )}
      </div>

      {/* Tabs */}
      <ControlledTabs tabs={TABS} activeTab={activeTab} onChange={setActiveTab}>
        {activeTab === 'agents' && (
          <div className="space-y-6">
            {/* Search and filters */}
            <div className="flex items-center gap-4">
              <div className="flex-1 max-w-md">
                <Input
                  ref={searchInputRef}
                  placeholder="Search agents..."
                  value={searchQuery}
                  onChange={(e: { target: { value: string } }) => setSearchQuery(e.target.value)}
                />
              </div>
              {ROLE_CHIPS.map((chip) => {
                const enabled = enabledRoles.has(chip.role);
                return (
                  <button
                    key={chip.role}
                    type="button"
                    onClick={() => toggleRole(chip.role)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
                      enabled
                        ? 'bg-background text-foreground border-border hover:bg-accent'
                        : 'bg-muted text-muted-foreground border-muted line-through'
                    }`}
                  >
                    {enabled ? (
                      <Eye className="h-3 w-3" />
                    ) : (
                      <EyeOff className="h-3 w-3" />
                    )}
                    {chip.label}
                  </button>
                );
              })}
            </div>

            {/* Agents table */}
            <Table
              columns={columns}
              data={filteredAgents}
              keyExtractor={(agent) => agent.agentId}
              onRowClick={(agent) => navigate(routes.console.spaceAgent(spaceId, agent.agentId))}
              emptyMessage="No agents found. Create your first agent to get started."
              isLoading={isLoading}
            />

            {/* Publish dialog — shared with the agent detail page */}
            {publishTarget && (
              <PublishAgentModal
                agent={publishTarget}
                isOpen
                onClose={() => setPublishTarget(null)}
              />
            )}

            {/* Delete confirmation modal */}
            <ConfirmModal
              isOpen={deleteModal.isOpen}
              onClose={() => setDeleteModal({ isOpen: false, agent: null })}
              onConfirm={handleDelete}
              title="Delete Agent"
              message={`Are you sure you want to delete "${deleteModal.agent?.name}"? This action cannot be undone.`}
              confirmText="Delete"
              isLoading={deleteAgent.isPending}
            />
          </div>
        )}

        {activeTab === 'schedules' && <ScheduleList />}
        {activeTab === 'subscriptions' && <SubscriptionList />}
      </ControlledTabs>
    </div>
  );
}
